import React, {
  useRef,
  useEffect,
  useState,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from "react";
import { fnGetDataSet } from "@shared/api/restApi";
import {
  getCodeData,
  getInitFormData,
  getInitSheet,
  parseDate,
} from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { fnSave } from "@shared/api/restApi";
import SnSheet from "@shared/components/Grid/SnSheet";
import { globalInfo, menuInfo } from "@shared/type/CommonType";
import { showDialog } from "src/widgets/CommonDialog";
import SnResizableLNR from "@shared/components/SnResizableLNR";
import SnButton from "@shared/components/SnButton";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import { IconButton, Tooltip } from "@mui/material";

type AssetItem = {
  no_asset: string;
  no_line?: number;
  dt_rcv?: string;
  cd_have?: string;
  cd_dept?: string;
  nm_dept?: string;
  cd_emp?: string;
  nm_emp?: string;
  dc_rmk?: string;
};

interface Props {
  Menuprops: menuInfo;
  PkFields: string[]; // PK 필드 목록
  tableName: string; // 저장할 테이블 이름
  controllerName: string; // 저장할 API 컨트롤러 이름
  userInfo: globalInfo["userInfo"];
}

const EXC000000000029003_POP_001 = forwardRef(
  (
    { Menuprops, PkFields, tableName, controllerName, userInfo }: Props,
    ref
  ) => {
    const [columns, setColums] = useState<any[]>([]);
    const [dataSource1, setDataSource1] = useState<any>();
    const gridref1 = useRef<any>(null);

    //#region grid 길이 관련
    let width1 = "99%";
    const menuSize = localStorage.getItem("menuSize");
    if (menuSize) {
      const menu = JSON.parse(menuSize);
      if (menu.ExcelUpload) {
        width1 = menu.ExcelUpload.grid1Width
          ? menu.ExcelUpload.grid1Width
          : width1;
      }
    }
    const [grid1Width, setGrid1Width] = useState<string | number>(width1);

    const [compareFields, setCompareFields] = useState<string[]>(PkFields); // 비교할 필드를 상태로 저장
    const [codeDataList, setCodeDataList] = useState<Record<string, string>[]>(
      []
    ); // 비교 대상 테이블 data tableName

    const [assetMap, setAssetMap] = useState<Map<string, AssetItem>>(new Map());

    const loadAssetList = async () => {
      const assetList = await getCodeData(tableName, "");
      setAssetMap(
        new Map(
          assetList.map((item: { no_asset: any }) => [item.no_asset, item])
        )
      );
    };

    //  tableName list

    //  메뉴에서 `cd_clas`, `cd_popup` 데이터를 한 번만 로드
    const [codeDataMap, setCodeDataMap] = useState(new Map());
    const [popupDataMap, setPopupDataMap] = useState(new Map());

    //#endregion

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitSheet({});

          // 오류내용 칼럼 수동 추가
          rtn.grid.sheet1.push({
            cd_column: "error_msg",
            nm_column: "오류내용",
            qt_width: 200,
            tp_column: "text",
            fg_visible: "Y",
            yn_required: "N",
            dc_editcon: "1 == 1",
            editable: false, // 수정 불가
          });

          setColums(rtn.grid.sheet1);

          gridref1.current.resetGridData();
        } catch (error) {}
      };
      fetchform();
      loadAssetList();
    }, []);

    // useEffect(() => {
    //   if (gridref1.current) {
    //     gridref1.current.setCodeDataList(codeDataList); //  SnSheet 내부에서 코드 데이터 갱신 메서드 실행
    //   }
    // }, [codeDataList]); //  codeDataList가 변경될 때마다 실행

    const onDelete = () => {
      const focusedCell = gridref1.current.api.getFocusedCell();
      let startRowIndex = focusedCell.rowIndex ?? 0;

      gridref1.current?.deleteRowByIndex(startRowIndex);
    };

    const normalizeDate = (val: string) => val?.replace(/[-/]/g, "");

    const onSave = async () => {
      try {
        const isValid = await Validation();
        if (!isValid) {
          return;
        }

        const dt: any[] = [];
        const insertList: any[] = [];
        const updateList: any[] = [];
        const dateFields = ["dt_start", "dt_return_plan"];

        gridref1.current.api.forEachNode((node: { data: any }) => {
          const row = node.data;

          // 1. 빈 줄은 스킵
          const isEmptyRow = [
            "no_asset",
            "dt_start",
            // "dt_return_plan",
            "cd_dept",
            "cd_emp",
          ].every((field) => !row[field]);
          if (isEmptyRow) return;

          const saveRow = { ...row };

          // 날짜 필드 정리
          dateFields.forEach((field) => {
            if (saveRow[field]) saveRow[field] = normalizeDate(saveRow[field]);
          });

          const asset = assetMap.get(saveRow.no_asset);
          if (!asset) return;

          const rowLineNo = Number(saveRow.no_line ?? 0);
          const assetLineNo = Number(asset.no_line ?? 0);

          if (rowLineNo > assetLineNo) {
            insertList.push(saveRow); //  신규 차수 → insert
          } else {
            updateList.push(saveRow); //  기존 차수 → update
          }
        });

        const SaveData: any = {};
        SaveData[tableName] = {
          insert: insertList,
          update: updateList,
          modifiedRowcount: insertList.length + updateList.length,
        };

        if (!SaveData[tableName].insert && !SaveData[tableName].update) {
          await showDialog("alter", "변경된 내용이 없습니다.");
          return;
        }

        console.log("SaveData:", JSON.stringify(SaveData, null, 2));

        await fnSave(controllerName, SaveData);
        gridref1.current.resetUpdate();
        gridref1.current.resetGridData();
      } catch (error: any) {}
    };

    // grid1의 width resize
    const onResizeStopH = (width: number) => {
      setGrid1Width(width);
    };

    useImperativeHandle(ref, () => ({
      onDelete,
      onSave,
    }));

    const isEmptyRow = (row: any): boolean => {
      const checkFields = [
        "no_asset",
        "dt_start",
        // "dt_return_plan",
        "cd_dept",
        "cd_emp",
      ];
      return checkFields.every((field) => !row[field]);
    };

    const Validation = async (): Promise<boolean> => {
      //  Validation 실행하여 오류 체크

      const validDateRegex =
        /^(19|20)\d{2}(\/|-)?(0[1-9]|1[0-2])(\/|-)?(0[1-9]|[12][0-9]|3[01])$/;

      if (gridref1.current.api.getDisplayedRowCount() <= 1) {
        await showDialog("alter", "처리할 내용이 없습니다.");
        return true;
      }

      let hasError = false;
      const assetSet = new Set<string>(); // 중복 체크용 Set

      gridref1.current.api.forEachNode((node: any) => {
        const row = node.data;
        if (!row || isEmptyRow(row)) return; // 빈 줄은 skip

        let errorMsg = "";
        const assetInfo = assetMap.get(row.no_asset) as any;

        if (assetSet.has(row.no_asset)) {
          errorMsg = "자산번호 중복";
          gridref1.current.api
            .getRowNode(node.id)
            .setDataValue("error_msg", errorMsg);
          hasError = true;
          return;
        } else {
          assetSet.add(row.no_asset);
        }

        // 1. 자산번호가 MTL_ASSET에 없음
        if (!assetInfo) {
          errorMsg = "자산번호가 존재하지 않습니다.";
          gridref1.current.api
            .getRowNode(node.id)
            .setDataValue("error_msg", errorMsg);
          hasError = true;
          return;
        } else {
          const cd_have = assetInfo.cd_have;

          // 2. 상태값이 rcv 또는 use일 때만 dt_start 입력 가능
          if (cd_have !== "RCV" && cd_have !== "USE") {
            if (cd_have === "RTN") {
              errorMsg = "반납된 자산입니다.";
            } else if (cd_have === "REQ") {
              errorMsg = "자산교체 또는 계약변경 중인 자산입니다.";
            }
            gridref1.current.api
              .getRowNode(node.id)
              .setDataValue("error_msg", errorMsg);
            hasError = true;
            return;
          }
        }

        const dateErrors: string[] = [];

        //  날짜 형식 Validation 추가
        let dateFields = ["dt_start", "dt_return_plan"];
        for (const field of dateFields) {
          const value = row[field];
          const column = columns.find((col) => col.cd_column === field); //  컬럼 정보 찾기
          const columnName = column?.nm_column ?? field; // 못 찾으면 field fallback

          if (
            value &&
            typeof value === "string" &&
            !validDateRegex.test(value)
          ) {
            dateErrors.push(`${columnName} 날짜 형식 오류`);
          }
        }

        //   Validation 추가
        dateFields = [
          "dt_start",
          //"dt_return_plan",
          //"cd_emp",
          //"nm_dept",
          //"nm_emp",
        ];
        for (const field of dateFields) {
          const value = row[field];
          const column = columns.find((col) => col.cd_column === field); //  컬럼 정보 찾기
          const columnName = column?.nm_column ?? field; // 못 찾으면 field fallback

          if (!value || value.trim() === "") {
            dateErrors.push(`${columnName} 누락`);
          }
        }

        const dtStart = row["dt_start"];
        const dtRcv = assetInfo.dt_rcv;

        if (dtStart && dtRcv) {
          const parsedStart = parseDate(dtStart);
          const parsedRcv = parseDate(dtRcv);

          if (parsedStart && parsedRcv && parsedStart < parsedRcv) {
            dateErrors.push(
              "사용시작일은 입고일자 이전으로 입력할 수 없습니다."
            );
          }
        }

        if (dateErrors.length > 0) {
          errorMsg += (errorMsg ? ", " : "") + dateErrors.join(", ");
        }

        if (errorMsg) {
          hasError = true;
          gridref1.current.api
            .getRowNode(node.id)
            .setDataValue("error_msg", errorMsg);
        } else {
          const targetFields = ["dt_start", "nm_dept", "cd_emp", "nm_emp"]; //
          let isChanged = false;

          for (const field of targetFields) {
            if (
              assetInfo[field] !== undefined &&
              row[field] !== assetInfo[field]
            ) {
              row["no_line"] = (assetInfo["no_line"] ?? 0) + 1;
              row["cd_user_reg"] = userInfo.cd_user;
              row["cd_firm"] = userInfo.cd_firm;
              row["yn_end"] = "Y";
              break; // 하나라도 다르면 증가시키고 끝
            } else {
              row["cd_user_amd"] = userInfo.cd_user;
              row["cd_firm"] = userInfo.cd_firm;
              row["yn_end"] = assetInfo["yn_end"];
            }
          }

          gridref1.current.api
            .getRowNode(node.id)
            .setDataValue("error_msg", "");
        }
      });

      //   const isValid = await gridref1.current.validateData();

      if (hasError) {
        showDialog("error", "오류내역을 확인해주세요.");
        return false;
      } else {
        return true;
      }
    };

    const onGrid1BtnsClick = async (id: string) => {
      if (id == "aButton_Init") {
        await loadAssetList(); //  자산정보 재로딩

        gridref1.current.resetGridData();
      }

      if (id == "aButton_Validation") {
        const isValid = await Validation();
        if (isValid) {
          showDialog("alter", "오류가 없습니다.");
        }
      }
    };

    return (
      <div className="h-full flex flex-col">
        <div className="w-full flex flex-row space-x-2 flex-grow overflow-x-auto">
          <SnResizableLNR
            width={grid1Width}
            minWidth={130}
            onResizeStop={onResizeStopH}
            enable={{ right: true }}
            menuName={Menuprops.cd_menu ?? ""}
            defaultSize={{ grid2Width: grid1Width }}
          >
            <SnSheet
              ref={gridref1}
              enableAddRow={true}
              columnConfig={columns}
              rowData={dataSource1}
              isEditable={true} // 수정 가능여부
              codeDataList={codeDataList} // pk 체크용 데이터 전달
              compareFields={compareFields} // 비교할 필드 정보 전달
              title={Menuprops.nm_menu}
              header={
                <div>
                  <SnButton
                    text={"초기화"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Init");
                    }}
                  />
                  <SnButton
                    text={"오류체크"}
                    onClick={() => {
                      onGrid1BtnsClick("aButton_Validation");
                    }}
                  />
                  <Tooltip title="Save">
                    <IconButton
                      onClick={onSave}
                      sx={{
                        color: "gray", // 기본 색상
                        "&:hover": {
                          color: "blue", // hover 시 색상
                          transform: "scale(1.2)", // hover 시 크기 확대
                          transition: "all 0.3s ease", // 부드러운 애니메이션
                        },
                      }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Tooltip>

                  <Tooltip title="Delete">
                    <IconButton
                      onClick={onDelete}
                      sx={{
                        color: "gray", // 기본 색상
                        "&:hover": {
                          color: "blue", // hover 시 색상
                          transform: "scale(1.2)", // hover 시 크기 확대
                          transition: "all 0.3s ease", // 부드러운 애니메이션
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              }
            />
          </SnResizableLNR>
        </div>
      </div>
    );
  }
);

export default EXC000000000029003_POP_001;
